import React, { useState, useEffect } from 'react';
import { FaRocket, FaMobileAlt, FaBell, FaSave } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import { useI18n } from '../contexts/I18nContext';
import { isTWAEnvironment, isPWAEnvironment } from '../utils/platformDetection';
import { trackInstallAccept, trackInstallDismiss } from '../services/analytics';
import '../styles/InstallPrompt.css';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

type InstallState =
  | 'not-installed'
  | 'can-install'
  | 'installing'
  | 'installed'
  | 'manual-install'
  | 'installed-but-in-browser';

interface BrowserInfo {
  name: string;
  isMobile: boolean;
  supportsOneClickInstall: boolean;
  instructionKey: string;
}

interface InstallPromptProps {
  forceInstall?: boolean;
}

const APP_NAME = 'CourseyVoyage';

const InstallPrompt: React.FC<InstallPromptProps> = ({ forceInstall = false }) => {
  const { t } = useI18n();
  const [installState, setInstallState] = useState<InstallState>('not-installed');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDesktopAppSuggestion, setShowDesktopAppSuggestion] = useState(true);

  const detectBrowserInfo = (): BrowserInfo => {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);

    if (ua.includes('chrome') && !ua.includes('edg')) {
      return {
        name: ua.includes('android') ? 'Chrome Android' : 'Chrome Desktop',
        isMobile,
        supportsOneClickInstall: true,
        instructionKey: ua.includes('android') ? 'chromeAndroid' : 'chromeDesktop',
      };
    }
    if (ua.includes('edg')) {
      return {
        name: 'Edge',
        isMobile,
        supportsOneClickInstall: true,
        instructionKey: isMobile ? 'edgeMobile' : 'edgeDesktop',
      };
    }
    if ((ua.includes('safari') || ua.includes('iphone') || ua.includes('ipad')) && !ua.includes('chrome')) {
      return {
        name: 'Safari iOS',
        isMobile: true,
        supportsOneClickInstall: false,
        instructionKey: 'safariIOS',
      };
    }
    if (ua.includes('firefox')) {
      return {
        name: 'Firefox',
        isMobile,
        supportsOneClickInstall: false,
        instructionKey: isMobile ? 'firefoxMobile' : 'firefoxDesktop',
      };
    }
    if (ua.includes('samsungbrowser')) {
      return {
        name: 'Samsung Internet',
        isMobile: true,
        supportsOneClickInstall: true,
        instructionKey: 'samsungInternet',
      };
    }
    return {
      name: 'Generic',
      isMobile,
      supportsOneClickInstall: false,
      instructionKey: isMobile ? 'genericMobile' : 'genericDesktop',
    };
  };

  const checkPWAStatus = () => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://');
    const params = new URLSearchParams(window.location.search);
    const isPWASource = params.get('source') === 'pwa';
    const savedAsPWA = localStorage.getItem('runningAsPWA') === 'true';
    const userAcceptedInstall = localStorage.getItem('userAcceptedInstall') === 'true';

    if (isPWASource || isStandalone) {
      localStorage.setItem('runningAsPWA', 'true');
    }

    const isCurrentlyInPWA = isStandalone || isPWASource;
    const wasPWAInstalled = userAcceptedInstall || savedAsPWA;

    if (isCurrentlyInPWA && !savedAsPWA) {
      localStorage.setItem('runningAsPWA', 'true');
      localStorage.setItem('userAcceptedInstall', 'true');
    }

    return {
      isCurrentlyInPWA,
      wasPWAInstalled,
      isInstalled: isCurrentlyInPWA || wasPWAInstalled,
    };
  };

  useEffect(() => {
    const browser = detectBrowserInfo();
    setBrowserInfo(browser);

    if (isTWAEnvironment()) {
      setInstallState('installed');
      setShowPrompt(false);
      return;
    }
    if (isPWAEnvironment()) {
      setInstallState('installed');
      setShowPrompt(false);
      return;
    }

    if (!browser.isMobile && !forceInstall) return;

    const pwaStatus = checkPWAStatus();
    if (pwaStatus.isCurrentlyInPWA) {
      setInstallState('installed');
      setShowPrompt(false);
      return;
    }
    if (pwaStatus.wasPWAInstalled) {
      setInstallState('installed-but-in-browser');
      const dismissed = localStorage.getItem('desktopAppSuggestionDismissed') === 'true';
      if (!dismissed && !forceInstall) {
        setShowPrompt(true);
        setShowDesktopAppSuggestion(true);
      } else {
        setShowPrompt(false);
      }
      return;
    }

    if (localStorage.getItem('installPromptDismissed') === 'true' && !forceInstall) {
      setInstallDismissed(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (localStorage.getItem('userAcceptedInstall') === 'true') {
        localStorage.removeItem('userAcceptedInstall');
        localStorage.removeItem('runningAsPWA');
      }
      setInstallState('can-install');
      setShowPrompt(true);
    };

    const handleAppInstalled = () => {
      trackInstallAccept();
      setInstallState('installed');
      setDeferredPrompt(null);
      localStorage.setItem('userAcceptedInstall', 'true');
      setShowPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    if (browser.isMobile && !pwaStatus.isInstalled) {
      setShowPrompt(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [forceInstall]);

  const installApp = async () => {
    if (!deferredPrompt) {
      setInstallState('manual-install');
      setShowInstructions(true);
      return;
    }
    try {
      setInstallState('installing');
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        trackInstallAccept();
        setInstallState('installed');
        localStorage.setItem('userAcceptedInstall', 'true');
        setShowPrompt(false);
      } else {
        trackInstallDismiss();
        setInstallState('not-installed');
        if (!forceInstall) {
          localStorage.setItem('installPromptDismissed', 'true');
          setInstallDismissed(true);
          setShowPrompt(false);
        }
      }
      setDeferredPrompt(null);
    } catch {
      setInstallState('manual-install');
      setShowInstructions(true);
    }
  };

  const handleClose = () => {
    trackInstallDismiss();
    setShowPrompt(false);
    setShowInstructions(false);
    setShowDesktopAppSuggestion(false);
  };

  const handleDontShowAgain = () => {
    trackInstallDismiss();
    setShowPrompt(false);
    setShowInstructions(false);
    setShowDesktopAppSuggestion(false);
    if (installState === 'installed-but-in-browser') {
      localStorage.setItem('desktopAppSuggestionDismissed', 'true');
    } else {
      localStorage.setItem('installPromptDismissed', 'true');
      setInstallDismissed(true);
    }
  };

  if (installState === 'installed') return null;

  if (installState === 'installed-but-in-browser' && showDesktopAppSuggestion) {
    return (
      <div className="install-prompt-overlay">
        <div className="install-prompt-container">
          <h2 className="install-prompt-title">{t('install.useDesktopApp')}</h2>
          <div className="pwa-benefits">
            <h3>{t('install.desktopHint')}</h3>
            <ul className="benefits-list">
              <li><span className="benefit-icon"><IconWrapper icon={FaRocket} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit1')}</strong></li>
              <li><span className="benefit-icon"><IconWrapper icon={FaMobileAlt} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit2')}</strong></li>
              <li><span className="benefit-icon"><IconWrapper icon={FaBell} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit3')}</strong></li>
              <li><span className="benefit-icon"><IconWrapper icon={FaSave} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit4')}</strong></li>
            </ul>
          </div>
          <div className="install-prompt-buttons">
            <button className="install-prompt-button close-button" onClick={handleDontShowAgain}>
              {t('install.dontShowAgain')}
            </button>
            <button className="install-prompt-button close-button" onClick={handleClose}>
              {t('install.continueBrowser')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (browserInfo && !browserInfo.isMobile && !forceInstall) return null;
  if (!showPrompt) return null;
  if (installDismissed && !forceInstall) return null;

  if (installState === 'installing') {
    return (
      <div className="install-prompt-overlay">
        <div className="install-prompt-container">
          <h2 className="install-prompt-title">{t('install.installing')}</h2>
          <div className="install-prompt-loading">
            <div className="install-prompt-spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (showInstructions && browserInfo) {
    return (
      <div className="install-prompt-overlay">
        <div className="install-prompt-container install-instructions-container">
          <h2 className="install-prompt-title">{t('install.howToInstall', { name: APP_NAME })}</h2>
          <div className="browser-info">
            <p><strong>{t('install.youAreUsing')}</strong> {browserInfo.name}</p>
          </div>
          <div className="install-instructions">
            {browserInfo.name === 'Chrome Android' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊瀏覽器右上角 <strong>三點選單 (⋮)</strong></li>
                  <li>選擇 <strong>「安裝應用程式」</strong> 或 <strong>「新增至主畫面」</strong></li>
                  <li>點擊 <strong>「安裝」</strong></li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Chrome Desktop' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊網址列右側 <strong>安裝圖示 (⬇)</strong></li>
                  <li>或右上角 <strong>三點選單</strong> → <strong>「安裝 {APP_NAME}」</strong></li>
                  <li>點擊 <strong>「安裝」</strong></li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Safari iOS' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊 <strong>分享按鈕 (↑)</strong></li>
                  <li>找到 <strong>「加入主畫面」</strong></li>
                  <li>點擊 <strong>「加入」</strong></li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Edge' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊網址列右側 <strong>安裝圖示</strong></li>
                  <li>或 <strong>三點選單</strong> → <strong>「應用程式」</strong> → <strong>「安裝此網站」</strong></li>
                  <li>點擊 <strong>「安裝」</strong></li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Firefox' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊網址列右側安裝圖示（如可見）</li>
                  <li>或選單中尋找 <strong>「安裝」</strong></li>
                  <li>建議使用 Chrome 或 Edge 以獲得最佳 PWA 體驗</li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Samsung Internet' && (
              <div className="instruction-steps">
                <ol>
                  <li>點擊右下角 <strong>選單</strong></li>
                  <li>選擇 <strong>「新增至主畫面」</strong></li>
                  <li>點擊 <strong>「新增」</strong></li>
                </ol>
              </div>
            )}
            {browserInfo.name === 'Generic' && (
              <div className="instruction-steps">
                <ol>
                  <li>尋找 <strong>「安裝應用程式」</strong> 或 <strong>「新增至主畫面」</strong></li>
                  <li>依照瀏覽器指示操作</li>
                  <li>建議使用 Chrome 或 Edge</li>
                </ol>
              </div>
            )}
          </div>
          <div className="install-prompt-buttons">
            <button className="install-prompt-button close-button" onClick={() => setShowInstructions(false)}>
              {t('install.back')}
            </button>
            <button className="install-prompt-button close-button" onClick={handleClose}>
              {t('install.later')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="install-prompt-overlay">
      <div className="install-prompt-container">
        <h2 className="install-prompt-title">{t('install.title', { name: APP_NAME })}</h2>
        <div className="pwa-benefits">
          <h3>{t('install.benefits')}</h3>
          <ul className="benefits-list">
            <li><span className="benefit-icon"><IconWrapper icon={FaRocket} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit1')}</strong></li>
            <li><span className="benefit-icon"><IconWrapper icon={FaMobileAlt} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit2')}</strong></li>
            <li><span className="benefit-icon"><IconWrapper icon={FaBell} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit3')}</strong></li>
            <li><span className="benefit-icon"><IconWrapper icon={FaSave} size={18} style={{ color: 'var(--primary-color)' }} /></span><strong>{t('install.benefit4')}</strong></li>
          </ul>
        </div>
        <div className="install-actions">
          {/* 優先顯示「立即安裝」：有 deferredPrompt 或瀏覽器支援一鍵安裝（Chrome/Edge/Samsung 等）。
              僅在確定不支援一鍵安裝時（如 Safari iOS、Firefox）才顯示「查看安裝方法」。
              點擊「立即安裝」時若尚未收到 beforeinstallprompt，會自動改顯示安裝步驟。 */}
          {(deferredPrompt || browserInfo?.supportsOneClickInstall) ? (
            <div className="one-click-install">
              <button className="install-prompt-button install-button primary-button" onClick={installApp}>
                {t('install.installNow')}
              </button>
              <p className="install-note">
                {deferredPrompt ? t('install.oneClickNote') : t('install.manualNote')}
              </p>
            </div>
          ) : (
            <div className="manual-install">
              <button className="install-prompt-button install-button primary-button" onClick={() => setShowInstructions(true)}>
                {t('install.viewInstructions')}
              </button>
              <p className="install-note">{t('install.manualNote')}</p>
            </div>
          )}
        </div>
        <div className="install-prompt-buttons">
          <button className="install-prompt-button close-button" onClick={handleDontShowAgain}>
            {t('install.dontShowAgain')}
          </button>
          <button className="install-prompt-button close-button" onClick={handleClose}>
            {t('install.later')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
